import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LMLoggerService, LMModalDialogWrapperRef, LMModalWrapperService, LMUrlUtilService } from '@app/services';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LMDialogButtonKey, LMGlobalUtilService, LMUniqueIdService } from '@app/utils';
import { LMFacadePickerDialogComponent } from '@app/components';
import { ListResource } from '@app/resources';
import { LMThemeService } from '@banner/lm-theme.service';

@Injectable()
export class LMFacadeService {

    private businessClassSelectUrl = '/list/BusinessView.ReportBusinessClassSelectList?output=spec&type=select&select=BusinessView.BusinessView';
    private categorySelectUrl = '/list/ReportCategory.ActiveReportCategorySelectList?output=spec&type=select&select=ReportCategory.ReportCategory';
    private moduleFacade$!: Subject<Array<LMModuleFacade> | undefined>;
    private moduleFacades: Array<LMModuleFacade> | undefined;
    private allBusinessClassListMap = new Map<string, Array<LMFacadeReportTemplate>>();
    private moduleColors: LMModuleColors | undefined;

    constructor(
        protected httpClient: HttpClient,
        protected lmLoggerService: LMLoggerService,
        protected lmUrlUtilService: LMUrlUtilService,
        protected lmUniqueIdService: LMUniqueIdService,
        protected lmModalWrapperService: LMModalWrapperService,
        protected lmThemeService: LMThemeService,
        protected lmGlobalUtilService: LMGlobalUtilService
        ) {
    }

    public getModuleFacades(): Observable<Array<LMModuleFacade> | undefined> {
        if (this.moduleFacades !== undefined) {
            return of(this.moduleFacades);
        }

        if (this.moduleFacade$ !== undefined) {
            return this.moduleFacade$;
        } else {
            this.moduleFacade$ = new Subject<Array<LMModuleFacade> | undefined>();
            let url = this.lmUrlUtilService.getFacadeUrl('Explorer');
            const params: LMUrlParams = { output: 'spec' };
            url = this.lmUrlUtilService.updateUrlParams(url, params);

            this.execute(url).subscribe({
                next: (v)   => { this.handleModuleFacadesResponse(v);   },
                error: (e)  => { this.moduleFacade$.error(e);           },
                complete: ()      => {                                        }
            });

            return this.moduleFacade$.asObservable();
        }
    }

    private handleModuleFacadesResponse(response: LMModuleFacadeResponse) {
        this.moduleFacades = response.facades;
        this.moduleFacade$.next(this.moduleFacades);
    }

    public getAllLists(businessClass: string): Observable<Array<LMFacadeReportTemplate>> {
        if (this.allBusinessClassListMap.has(businessClass)) {
            const data = this.allBusinessClassListMap.get(businessClass);
            return of(data ? data : []);
        }

        const subject$ = new Subject<Array<LMFacadeReportTemplate>>();
        let url = this.lmUrlUtilService.getFacadeUrl('Explorer');
        const params: LMUrlParams = { requestType: 'all-lists' };
        url = this.lmUrlUtilService.addParamToUrl(url, 'businessClass', businessClass);
        url = this.lmUrlUtilService.updateUrlParams(url, params);

        this.execute(url).subscribe({
            next:       (v)=>   { this.handleAllListsResponse(subject$, v);   },
            error:      (e)=>   { subject$.error(e);                              },
            complete:   ( )         =>      {                                                    }
        });

        return subject$.asObservable();
    }

    private handleAllListsResponse(subject$: Subject<Array<LMFacadeReportTemplate>>, response: LMFacadeAllListsResponse) {
        this.allBusinessClassListMap.set(response.businessClass, response.allLists);
        subject$.next(response.allLists);
        subject$.complete();
    }

    public getTemplates(): Observable<LMFacadeSuggestionResponse> {
        let url = this.lmUrlUtilService.getFacadeUrl('Explorer');
        const params: LMUrlParams = { requestType: 'templates' };
        url = this.lmUrlUtilService.updateUrlParams(url, params);
        return this.execute(url);
    }

    public getTemplateDetail(resource: ListResource, userFolder?: string): Observable<LMFacadeReportTemplateDetail> {
        let url = this.lmUrlUtilService.getFacadeUrl('Explorer');

        const params: LMUrlParams = {
            requestType: 'template-detail',
            list: resource.resourceId,
            userfolder: userFolder
        };

        url = this.lmUrlUtilService.updateUrlParams(url, params);

        return this.execute(url);
    }

    public getBusinessObject(businessClass: string): Observable<LMBusinessObject> {
        let url = this.lmUrlUtilService.getFacadeUrl('Explorer');

        const params: LMUrlParams = { requestType: 'business-object' };
        url = this.lmUrlUtilService.addParamToUrl(url, 'businessClass', businessClass);
        url = this.lmUrlUtilService.updateUrlParams(url, params);

        return this.execute(url);
    }

    public getModule(module: string): Observable<LMModule> {
        let url = this.lmUrlUtilService.getFacadeUrl('Explorer');

        const params: LMUrlParams = { requestType: 'module' };
        url = this.lmUrlUtilService.addParamToUrl(url, 'module', module);
        url = this.lmUrlUtilService.updateUrlParams(url, params);

        return this.execute(url);
    }

    public getSearchableFields(businessClassName: string): Observable<LMSearchableFields> {
        let url = this.lmUrlUtilService.getFacadeUrl('Explorer');

        const params: LMUrlParams = { requestType: 'searchable-fields' };
        url = this.lmUrlUtilService.addParamToUrl(url, 'businessClass', businessClassName);
        url = this.lmUrlUtilService.updateUrlParams(url, params);

        return this.execute(url);
    }

    public getBusinessClassFacade(businessClassName: string): Observable<LMBusinessClassFacade> {
        let url = this.lmUrlUtilService.getFacadeUrl('Explorer');

        const params: LMUrlParams = { requestType: 'business-class-facade' };
        url = this.lmUrlUtilService.addParamToUrl(url, 'businessClass', businessClassName);
        url = this.lmUrlUtilService.updateUrlParams(url, params);

        return this.execute(url);
    }

    public getGroupFieldFacade(businessClassName: string, groupField: string): Observable<LMBusinessClassFacade> {
        let url = this.lmUrlUtilService.getFacadeUrl('Explorer');

        const params: LMUrlParams = { requestType: 'group-field-facade' };
        url = this.lmUrlUtilService.addParamToUrl(url, 'businessClass', businessClassName);
        url = this.lmUrlUtilService.addParamToUrl(url, 'groupField', groupField);
        url = this.lmUrlUtilService.updateUrlParams(url, params);

        return this.execute(url);
    }

    public getColumnarFacade(businessClassName: string): Observable<LMColumnarFacade> {
        let url = this.lmUrlUtilService.getFacadeUrl('Explorer');

        const params: LMUrlParams = { requestType: 'columnar-facade' };
        url = this.lmUrlUtilService.addParamToUrl(url, 'businessClass', businessClassName);
        url = this.lmUrlUtilService.updateUrlParams(url, params);

        return this.execute(url);
    }

    private execute(url: string): Observable<any> {
        return this.httpClient
            .get(url).pipe(
                catchError(error => {
                    this.lmLoggerService.error(error);
                    return throwError(error || 'Server error');
                }));
    }

    public copyTemplate(resource: ListResource, userFolder?: string ) {
        let url = this.lmUrlUtilService.getBuilderUrl('CreateReport');

        const params: LMUrlParams = {
            list: resource.resourceId,
            userfolder: userFolder
        };

        url = this.lmUrlUtilService.updateUrlParams(url, params);

        this.lmGlobalUtilService.navigateByUrl(url);
    }

    public createFromScratch(resource: ListResource, facadeReportType: LMFacadeReportType, moduleTag: string) {
        let url = this.lmUrlUtilService.getBuilderUrl('CreateReport');
        let reportType: 'transaction' | 'columnar' | 'cube' = 'transaction';  // This is what the builder is expecting

        if (facadeReportType === 'analytic') {
            reportType = 'cube';
        } else if (facadeReportType === 'columnar') {
            reportType = 'columnar';
        }

        // 'live' | 'cube' | 'columnar'
        const params: LMUrlParams = {
            list: resource.resourceId,
            reporttype: reportType,
            moduletag: moduleTag
        };

        url = this.lmUrlUtilService.updateUrlParams(url, params);

        this.lmGlobalUtilService.navigateByUrl(url);
    }

    public searchBusinessObjects(searchTerms: LMFacadeSearchEvent, modules: Array<LMModuleFacade>): Array<LMSearchBusinessObject> {
        if (searchTerms) {
            if (searchTerms.categories.length > 0) {
                const filteredModules: Array<LMModuleFacade> = [];
                searchTerms.categories.forEach((category: string) => {
                    filteredModules.push( ...modules.filter((module: LMModuleFacade) => module.title.includes(category)));
                });
                return this.findBusinessObjects(searchTerms, filteredModules);
            } else {
                // don't need to restrict to specific modules
                // search thru business object names and descriptions
                return this.findBusinessObjects(searchTerms, modules);
            }
        }
        return [];
    }

    public searchTemplates(searchTerms: LMFacadeSearchEvent, templates: Array<LMFacadeReportTemplate>): Array<LMFacadeReportTemplate> {
        const filteredTemplates: Array<LMFacadeReportTemplate> = [];
        if (searchTerms) {
            if (searchTerms.categories.length > 0) {
                const filteredModules: Array<LMFacadeReportTemplate> = [];
                searchTerms.categories.forEach((category: string) => {
                    filteredModules.push( ...templates.filter((template: LMFacadeReportTemplate) => template.categoryName.includes(category)));
                });
                 return this.findTemplates(searchTerms, filteredModules);
            } else {
                return this.findTemplates(searchTerms, templates);
            }
        }
        return filteredTemplates;
    }

    public promptPickerDialog(type: LMFacadeSelectType, title: string): Observable<LMFacadeSelectDialogResult> {
        return new Observable<LMFacadeSelectDialogResult> (observer => {
            let dialogComponent: LMFacadePickerDialogComponent;
            // eslint-disable-next-line prefer-const
            let dialog: LMModalDialogWrapperRef<LMFacadePickerDialogComponent>;
            const dialogId = 'lm-module-facade-' + type +'-modal';

            const attributes: SohoAttributesArray = [ {
                name: 'data-automation-id',
                value: dialogId
            } ];

            const sohoModalOptions: SohoModalOptions = {
                fullsize: 'responsive',
                attributes
            };

            const buttons = [
                {
                    id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CANCEL ]),
                    text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
                    click: () => {
                        dialog.close(<LMFacadeSelectDialogResult> {
                            status: 0
                        });
                    }
                },
                {
                    id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.OK ]),
                    text: Soho.Locale.translate(LMDialogButtonKey.OK),
                    click: () => {
                        dialog.close(<LMFacadeSelectDialogResult> {
                            status: 1,
                            type: type,
                            target: dialogComponent.value
                        });
                    },
                    validate: false,
                    isDefault: true
                }
            ];
            dialog = this.lmModalWrapperService
              .modal(LMFacadePickerDialogComponent, sohoModalOptions)
              .buttons(buttons)
              .title(title)
              .isAlert(false)
              .open()
              .apply((pickerComponent: LMFacadePickerDialogComponent) => {
                  dialogComponent = pickerComponent;
                  pickerComponent.type = type;

                  if (type === 'businessClass') {
                      pickerComponent.label = Soho.Locale.translate('BusinessClass');

                  } else if (type === 'category') {
                      pickerComponent.label = Soho.Locale.translate('Categorization');
                  }
              })
              .afterClose((result: LMFacadeSelectDialogResult) => {
                  observer.next(result);
                  observer.complete();
              });
        });
    }

    public getSelectUrl(type: LMFacadeSelectType): string {
        let url = this.lmUrlUtilService.getBaseUrl();

        if (type === 'businessClass') {
            url = url + this.businessClassSelectUrl;
        } else if (type === 'category') {
            url = url + this.categorySelectUrl;
        }

        return this.lmUrlUtilService.propagateSessionKeys(url, '&');
    }

    public getFacadeFields(moduleTag: string, facade: LMBusinessClassFacade): Array<LMFacadeField>  {
        return this._getFields(moduleTag,  facade.fields);
    }

    public getFacadeConditions(moduleTag: string, facade: LMBusinessClassFacade): Array<LMFacadeField>  {
        return this._getFields(moduleTag,  facade.conditions);
    }

    public getFacadeColumnarDimension(moduleTag: string, facade: LMBusinessClassFacade): Array<LMFacadeField>  {
        return this._getFields(moduleTag,  facade.columnarDimensions);
    }

    public getFacadeColumnarMeasures(moduleTag: string, facade: LMBusinessClassFacade): Array<LMFacadeField>  {
        return this._getFields(moduleTag,  facade.columnarMeasures);
    }

    public getFacadeCubeDimensions(moduleTag: string, facade: LMBusinessClassFacade): Array<LMFacadeField>  {
        return this._getFields(moduleTag,  facade.cubeDimensions);
    }

    public getFacadeCubeMeasures(moduleTag: string, facade: LMBusinessClassFacade): Array<LMFacadeField>  {
        return this._getFields(moduleTag,  facade.cubeMeasures);
    }

    private _getFields(moduleTag: string, fields: Array<LMFacadeField>) {
        const response: Array<LMFacadeField> = [];

        fields.forEach(fieldFacade => {
            if (!fieldFacade.moduleTags || this.checkModuleTagsInclude(moduleTag, fieldFacade.moduleTags)) {
                const tempField = fieldFacade;
                tempField.displayName = this.buildFieldLabel(fieldFacade);

                if (fieldFacade.uiFieldSpec) {
                  tempField.icon = this.getFieldIcon(fieldFacade);
                  tempField.textTooltip = this.getFieldTypeTooltip(fieldFacade);
                  tempField.tooltip = this.buildPropertyHTML('fields', fieldFacade);
                }
              response.push(tempField);
            }
        });

        // alphabetize the fields
        response.sort((a: LMFacadeField, b: LMFacadeField) => (a.name || '' ).localeCompare(b.name || '' ));

        return response;
    }

    private buildFieldLabel(facadeField: LMFacadeField): string {
        if (facadeField.label) {
            if (facadeField.name && facadeField.name.trim() !== facadeField.label.trim() ) {
                return `${facadeField.label} (${facadeField.name})`;
            }
            return facadeField.label;
        }

        if (facadeField.name) {
            return facadeField.name;
        }

        if (facadeField?.uiFieldSpec.label) {
            return facadeField.uiFieldSpec.label;
        }

        return facadeField.fieldId;
    }
    public getFacadeFilters(moduleTag: string, facade: LMBusinessClassFacade): Array<LMFacadeFilter> {
        const filters: Array<LMFacadeFilter> = [];

        facade.filters.forEach(filterFacade => {
            if (!filterFacade.moduleTags || this.checkModuleTagsInclude(moduleTag, filterFacade.moduleTags)) {
                filterFacade.tooltip = this.buildPropertyHTML('filters', filterFacade);
                filters.push(filterFacade);
            }
        });

        return filters;
    }

    public getFacadeSortOrders(moduleTag: string, facade: LMBusinessClassFacade): Array<LMFacadeSortOrder> {
        const sortOrders: Array<LMFacadeSortOrder> = [];

        facade.sortOrders.forEach(sortOrderFacade => {
            if (!sortOrderFacade.moduleTags || this.checkModuleTagsInclude(moduleTag, sortOrderFacade.moduleTags)) {
                sortOrderFacade.tooltip = this.buildPropertyHTML('sort-orders', sortOrderFacade);
                sortOrders.push(sortOrderFacade);
            }
        });

        return sortOrders;
    }

    public getFacadeRelations(moduleTag: string, facade: LMBusinessClassFacade): Array<LMFacadeRelation> {
        const relations: Array<LMFacadeRelation> = [];

        facade.relations.forEach(relationFacade => {
            if (!relationFacade.moduleTags || this.checkModuleTagsInclude(moduleTag, relationFacade.moduleTags)) {
                relationFacade.tooltip = this.buildPropertyHTML('relations', relationFacade);
                relations.push(relationFacade);
            }
        });

        return relations;
    }

    public isColumnarAuditReportsEnabled(moduleTag: string, facade: LMBusinessClassFacade) : boolean {
        if (!facade.columnarAuditReports) {
            return false;
        }

        return this.checkFacadeEnablement(moduleTag, facade.columnarAuditReports);
    }

    public isColumnarReportsEnabled(moduleTag: string, facade: LMBusinessClassFacade) : boolean {
        if (!facade.columnarReports) {
            return false;
        }

      return this.checkFacadeEnablement(moduleTag, facade.columnarReports);
    }

    public isCubeViewReportsEnabled(moduleTag: string, facade: LMBusinessClassFacade) : boolean {
        if (!facade.cubeViewReports) {
            return false;
        }

        return this.checkFacadeEnablement(moduleTag, facade.cubeViewReports);
    }

    private checkFacadeEnablement(moduleTag: string, facadeEnablement: LMFacadeEnablement) : boolean {
        if (!facadeEnablement.moduleTags) {
            return facadeEnablement.enabled;
        }

        return this.checkModuleTagsInclude(moduleTag, facadeEnablement.moduleTags);
    }

    private checkModuleTagsInclude(moduleTag: string, moduleTags:LMFacadeModuleTags): boolean {
        if (moduleTags.tags.includes(moduleTag)) {
            return moduleTags.include;
        }

        return !moduleTags.include;
    }

    private findBusinessObjects(searchTerms: LMFacadeSearchEvent, facades: Array<LMModuleFacade>): Array<LMSearchBusinessObject> {
        const foundKeys:string[] = [];
        const filteredBusinessObjects: Array<LMSearchBusinessObject> = [];
        facades.forEach((module: LMModuleFacade, moduleIndex) => {
            module.sections.forEach((moduleSection: LMModuleFacadeSection, sectionIndex: number) => {
                moduleSection.businessObjects.forEach((businessObject: LMBusinessObject, boIndex: number) => {
                    if (searchTerms) {
                        let isMatch = false;
                        let key: string;
                        const boTitle = businessObject.title ? businessObject.title.toLowerCase() : undefined;
                        const boDescription = businessObject.description ? businessObject.description.toLowerCase() : undefined;
                        const searchValue = searchTerms.value.toLowerCase();

                        if (boTitle && boTitle.length > 0 && boTitle.includes(searchValue)) {
                            isMatch = true;
                        } else if (boDescription && boDescription.length > 0 && boDescription.includes(searchValue)) {
                            isMatch = true;
                        }

                        if (isMatch) {
                            key = `${businessObject.businessClass}-${businessObject.moduleTag}`;
                            if (!foundKeys.includes(key)) {
                                foundKeys.push(key);
                                const clonedBO: LMSearchBusinessObject = JSON.parse(JSON.stringify(businessObject));
                                clonedBO.uniqueId = moduleIndex + '_' + sectionIndex + '_' + boIndex;
                                filteredBusinessObjects.push(clonedBO);
                            }
                        }
                    }
                });
            });
        });
        return filteredBusinessObjects;
    }

    private findTemplates(searchTerms: LMFacadeSearchEvent, templates: Array<LMFacadeReportTemplate>): Array<LMFacadeReportTemplate> {
        const filteredTemplates: Array<LMFacadeReportTemplate> = [];
        templates.forEach((template: LMFacadeReportTemplate) => {
            if (searchTerms) {
                const templateReportName = template.reportName ? template.reportName.toLowerCase() : undefined;
                const templateDescription = template.description ? template.description.toLowerCase() : undefined;
                const searchValue = searchTerms.value.toLowerCase();
                if (templateReportName && templateReportName.length > 0 && templateReportName.includes(searchValue)) {
                    filteredTemplates.push(template);
                } else if (templateDescription && templateDescription.length > 0 && templateDescription.includes(searchValue)) {
                    filteredTemplates.push(template);
                }
            }
        });
        return filteredTemplates;
    }

    public buildInitials(value: string): string {
        const initials: Array<string> = [];
        value = value.trim();

        if (value.indexOf(' ') === -1) {
            value = value.replace(/([A-Z])/g, ' $1').trim();
        }

        const parts = value.split(' ');
        parts.forEach((p) => {
            p.split('').forEach((s, index) => {
                if (index === 0) {
                    initials.push(s);
                }
            });
        });
        return initials.join('');
    }

    public buildPropertyHTML(implementation: LMFacadeBusinessClassPanel, facadeComponent: LMFacadeComponent) {
        const labelStyle = `style="font-size: 1.4rem; opacity: .8; text-align: left; padding-bottom: 2px"`;
        const fieldStyle = `style="font-size: 1.4rem; text-align: left; overflow: hidden; text-overflow: ellipsis; padding-bottom: 12px"`;
        const html: Array<string> = [];

        // description
        if (facadeComponent.description) {
            html.push(`<p style="font-size: 1.4rem; opacity: .8; text-align: left; padding-bottom: 12px">${facadeComponent.description}</p>`);
        }
        // name
        html.push(`<div ${labelStyle}>${Soho.Locale.translate('Facade_PropertyName')}:</div>`);
        html.push(`<div title="${facadeComponent.name}" ${fieldStyle}>${facadeComponent.name}</div>`);


        switch (implementation) {
            case 'fields':
            case 'conditions':
            case 'columnar-dimensions':
            case 'columnar-measures':
            case 'cube-dimensions':
            case 'cube-measures':
                const field: LMFacadeField = facadeComponent as LMFacadeField;
                // field id
                html.push(`<div ${labelStyle}>${Soho.Locale.translate('FieldID')}:</div>`);
                html.push(`<div title="${field.fieldId}" ${fieldStyle}>${field.fieldId}</div>`);
                // default label
                const defaultLabel = field.label ? field.label : field.uiFieldSpec.label;
                html.push(`<div ${labelStyle}>${Soho.Locale.translate('Label')}:</div>`);
                html.push(`<div title="${defaultLabel}" ${fieldStyle}>${defaultLabel}</div>`);
                // field type =
                const fieldTypeLabel = Soho.Locale.translate('FieldType');
                // const fieldType
                if ('fields' === implementation) {
                    html.push(`<div ${labelStyle}>${Soho.Locale.translate('Facade_PropertyType')}:</div>`);
                    html.push(`<div title="${field.uiFieldSpec.lmrkFieldType}" ${fieldStyle}>${field.uiFieldSpec.fieldType}</div>`);
                }

                break;
            case 'filters':
                const filter: LMFacadeFilter = facadeComponent as LMFacadeFilter;
                // filter
                html.push(`<div ${labelStyle}>${Soho.Locale.translate('Facade_PropertyWhere')}:</div>`);
                html.push(`<div style="font-size: 1.4rem; text-align: left">${filter.where}</div>`);
                // include
                if (filter.include) {
                    html.push(`<div ${labelStyle}>${Soho.Locale.translate('Facade_PropertyInclude')}:</div>`);
                    html.push(`<div ${fieldStyle}>${filter.include}</div>`);
                }
                break;
            case 'relations':
                const relation: LMFacadeRelation = facadeComponent as LMFacadeRelation;
                html.push(`<div ${labelStyle}>${Soho.Locale.translate('Facade_PropertyRelation')}:</div>`);
                html.push(`<div title="${relation.relatedLink}" ${fieldStyle}>${relation.relatedLink}</div>`);
                break;
            case 'sort-orders':
                const sort: LMFacadeSortOrder = facadeComponent as LMFacadeSortOrder;
                html.push(`<div ${labelStyle}>${Soho.Locale.translate('Facade_PropertySortedBy')}:</div>`);

                for (let i = 0; i < sort.setSpec.sortFields.length; i++) {
                    html.push(`<div tyle="padding-bottom: 2px">`);
                    if (i > 0) {
                        html.push(`<span  style="font-size: 1.4rem; opacity: .8; text-align: left;">${Soho.Locale.translate('Then')} </span>`);
                    }
                    const field = sort.setSpec.sortFields[i];
                    html.push(`<span style="font-size: 1.4rem; text-align: left; overflow: hidden; text-overflow: ellipsis;" title="${field.name}">${field.name}`);
                    if (field.descending) {
                        html.push(`<svg class="icon" focusable="false" aria-hidden="true" role="presentation">`);
                        html.push(`<use xlink:href="#icon-sort">`);
                        html.push(`</svg>`);
                    }
                    html.push(`</span></div>`);
                }
                break;
        }

        return html.join('');
    }

  private getFieldTypeTooltip(facadeField: LMFacadeField): string {
    if (facadeField.uiFieldSpec.isKeyField) {
      return Soho.Locale.translate('Facade_FieldTypeKeyField');
    }
    switch (facadeField.uiFieldSpec.lmrkFieldType ) {
      case 'BOOLEAN':
        return Soho.Locale.translate('Facade_FieldTypeTrueOrFalse');
      case 'BCD':
      case 'NUMERIC':
      case 'PERCENT':
        return Soho.Locale.translate('Number');
      case 'TIMESTAMP':
      case 'YYYYMMDD':
      case 'YYYYMM':
      case 'YYYYWW':
      case 'YYYYQ':
      case 'YYYY':
      case 'MMDD':
      case 'TIME':
        return Soho.Locale.translate('Facade_FieldTypeDateOrTime');
      default:
        return Soho.Locale.translate('Facade_FieldTypeText');
    }
  }

  private getFieldIcon(facadeField: LMFacadeField): string {
    if (facadeField.uiFieldSpec.isKeyField) {
      return 'key';
    }
    switch (facadeField.uiFieldSpec.lmrkFieldType ) {
      case 'BOOLEAN':
        return 'check-list';
      case 'BCD':
      case 'NUMERIC':
      case 'PERCENT':
        return 'number';
      case 'TIMESTAMP':
      case 'YYYYMMDD':
      case 'YYYYMM':
      case 'YYYYWW':
      case 'YYYYQ':
      case 'YYYY':
      case 'MMDD':
      case 'TIME':
        return 'calendar';
      default:
        return 'change-font';
    }
  }

  public getModuleColor(moduleTag: string): string {
      if (this.moduleColors === undefined) {
        this.moduleColors = {};
      }
      if (this.moduleColors[moduleTag] !== null && this.moduleColors[moduleTag] !== undefined) {
        return this.moduleColors[moduleTag];
      } else {
        this.moduleColors[moduleTag] = this.lmThemeService.getBackgroundColorClass();
        return this.moduleColors[moduleTag];
      }
  }
}
